import {
  Box,
  BoxProps,
  ButtonProps,
  InputProps,
  SpinnerProps,
  TextProps
} from "@chakra-ui/react"
import {
  DarkMode,
  LightMode
} from "@pathwright/ux/src/components/chakra/ui/color-mode"
import classNames from "classnames"

type BGModeProps = {
  mode?: "dark" | "light"
  exclusions?: string[]
  text?: TextProps
  input?: InputProps
  button?: ButtonProps
  spinner?: SpinnerProps
} & BoxProps

function BGMode({
  mode = "dark",
  exclusions,
  text,
  input,
  button,
  spinner,
  ...props
}: BGModeProps) {
  const oppositeMode = mode === "dark" ? "light" : "dark"
  const ColorModeComponent = mode === "dark" ? DarkMode : LightMode

  const textStyles = {
    color: mode === "dark" ? "white" : undefined,
    ...text
  }

  const inputStyles: InputProps = {
    color: "gray.500",
    bg: "white !important",
    borderRadius: "20px",
    _placeholder: { color: "gray.500" },
    ...input
  }

  const inputPlaceholderStyles = {
    _placeholder: { color: "gray.500" },
    ...input?._placeholder
  }

  const buttonStyles: ButtonProps = {
    ...button
  }

  const spinnerStyles: SpinnerProps = {
    borderTopColor: "white",
    borderRightColor: "white",
    ...spinner
  }

  // Use :where to target elements within the component, but not within the whereNotSelector.
  function getWhereSelector(selector: string, whereNotSelector: string) {
    return `:where(${selector}):not(:where(${whereNotSelector}) :where(${selector}))`
  }

  // Use :where to target elements within the component, but not within the nested opposite mode.
  function getSelector(selector: string) {
    return `.chakra-theme.${mode} & ${getWhereSelector(
      selector,
      `.chakra-theme.${oppositeMode}`
    )}`
  }

  let css: NonNullable<BoxProps["css"]> = {
    ...props.css,
    // NOTE: using classnames instead of tag names since, at least with "p",
    // when using tag names it seems to affect other styling.

    // textStyles
    [getSelector(".chakra-heading")]: textStyles,
    [getSelector(".chakra-text, p")]: textStyles,
    [getSelector(".chakra-form__label")]: textStyles,
    [getSelector(".chakra-form__error-message")]: textStyles,
    [getSelector(".chakra-input__right-addon")]: textStyles,
    [getSelector(".chakra-checkbox__label")]: textStyles,
    // Only targetting icons outside of buttons, allowing icons within buttons to inherit color styles from the button.
    [getSelector(getWhereSelector(".chakra-icon", ".chakra-button"))]:
      textStyles,

    // inputStyles
    [getSelector(".chakra-input")]: inputStyles,
    [getSelector("input[id^='field-:r']")]: inputStyles,
    [getSelector("input[id^=':r']")]: inputStyles,
    [getSelector("input:-internal-autofill-previewed")]: inputStyles,
    [getSelector("input:-internal-autofill-selected")]: inputStyles,

    // inputPlaceholderStyles
    // Found in chakra-react-select.
    [getSelector("[id$='-placeholder']")]: inputPlaceholderStyles,

    // buttonStyles
    [getSelector("button.chakra-button")]: buttonStyles,
    [getSelector(".chakra-spinner")]: spinnerStyles
  }

  // Exclude the override styles from these selectors and their descendents.
  if (exclusions) {
    css = {
      [`& *${exclusions.map((selector) => `:not(${selector} *)`).join("")}`]:
        css
    }
  }

  return (
    <ColorModeComponent>
      <Box
        {...props}
        className={classNames("chakra-ui-reset-css", props.className)}
        css={css}
      />
    </ColorModeComponent>
  )
}

export default BGMode
